import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  Search,
  Heart,
  User,
  LogOut,
  ChevronDown,
  Shield,
  MapPin,
  Facebook,
} from "lucide-react";
import SearchOverlay from "./SearchOverlay";
import { useAuth } from "../context/AuthContext";
import { useWishlist } from "../context/WishlistContext";
import { useHero, useStoreSettings } from "../hooks/useHomeContent";
import { TOP_NAV } from "../data/homeContent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absolutize = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  return `${BACKEND_URL}${u}`;
};

/** Convert #rrggbb + 0..1 alpha to a rgba() string. */
function hexToRgba(hex, alpha) {
  const h = String(hex || "").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(full.slice(0, 2), 16) || 43;
  const g = parseInt(full.slice(2, 4), 16) || 27;
  const b = parseInt(full.slice(4, 6), 16) || 18;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, logout } = useAuth();
  const { count } = useWishlist();
  const store = useStoreSettings();
  const hero = useHero();

  // The hero page uses a full-screen video that plays *behind* the transparent
  // navbar. On every other route we keep the navbar solid (with its own bg).
  const overHero = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const firstName = user && user.name ? user.name.split(" ")[0] : "";
  // Logo: use the uploaded text-logo PNG when present, otherwise the image logo.
  // Cache-bust so freshly-uploaded logos appear immediately (no stale browser cache).
  const cacheKey = `${hero.textLogoImage || ""}|${hero.logoUrl || ""}`;
  const cacheBust = Math.abs(
    Array.from(cacheKey).reduce((a, c) => (a * 31 + c.charCodeAt(0)) | 0, 7)
  );
  const rawLogo = hero.textLogoImage
    ? absolutize(hero.textLogoImage)
    : absolutize(hero.logoUrl);
  const logoSrc = rawLogo
    ? `${rawLogo}${rawLogo.includes("?") ? "&" : "?"}v=${cacheBust}`
    : rawLogo;

  const headerClass = [
    "tk-header",
    "tk-header-prada",
    "tk-header-solid-blur", // always frosted-blur; no more hover-to-frost swap
    overHero ? "tk-header-over" : "tk-header-solid",
    scrolled ? "scrolled" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <header
        className={headerClass}
        style={{
          // Convert 0-100 sliders → rgba alpha for the frosted bar.
          "--nav-alpha": Math.max(0, Math.min(100, hero.navOpacity)) / 100,
        }}
      >
        {/* Row 1 — three-column layout: [Menu · Search] [centered logo] [Visit · Wishlist · Login] */}
        <div className="tk-header-top">
          <div className="tk-header-edge tk-header-row tk-header-prada-row">
            {/* LEFT — Menu (opens slide-in drawer) + Search */}
            <div className="tk-tools tk-tools-left">
              <button
                type="button"
                className="tk-tool tk-tool-menu"
                aria-label="Open menu"
                onClick={() => setOpen(true)}
                data-testid="header-menu"
              >
                <Menu size={20} />
                <span>Menu</span>
              </button>
              <button
                type="button"
                className="tk-tool"
                aria-label="Search designs"
                onClick={() => setSearchOpen(true)}
                data-testid="header-search"
              >
                <Search size={18} />
                <span>Search</span>
              </button>
            </div>

            {/* CENTER — Prada-style logo, image only (or admin's text-logo PNG) */}
            <Link to="/" className="tk-logo-center" data-testid="header-logo">
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt="Alankar Jewellers"
                  style={{ width: `${hero.logoWidth}px` }}
                />
              ) : (
                // No default logo — center intentionally empty until admin uploads
                <span className="tk-logo-empty" aria-hidden="true" />
              )}
            </Link>

            {/* RIGHT — Visit Store · Wishlist · Login / user avatar */}
            <div className="tk-tools tk-tools-right">
              <a
                className="tk-tool"
                target="_top"
                rel="noreferrer"
                href={store.mapUrl}
                data-testid="header-visit-store"
              >
                <MapPin size={18} />
                <span>Visit Store</span>
              </a>

              <Link
                to="/wishlist"
                className="tk-tool tk-tool-wl"
                aria-label={`Wishlist (${count})`}
                data-testid="header-wishlist"
              >
                <Heart size={18} />
                <span>Wishlist</span>
                {count > 0 && <span className="tk-wl-count">{count}</span>}
              </Link>

              {user ? (
                <div className="tk-user" ref={menuRef}>
                  <button
                    type="button"
                    className="tk-user-btn"
                    onClick={() => setUserMenuOpen((s) => !s)}
                    aria-haspopup="menu"
                    aria-expanded={userMenuOpen}
                    data-testid="header-user-menu"
                  >
                    <span className="tk-user-initial">
                      {firstName ? firstName[0].toUpperCase() : <User size={14} />}
                    </span>
                    <span className="tk-user-name">Hi, {firstName}</span>
                    <ChevronDown size={13} />
                  </button>
                  {userMenuOpen && (
                    <div className="tk-user-menu" role="menu">
                      <div className="tk-user-info">
                        <strong>{user.name}</strong>
                        <span>{user.email}</span>
                      </div>
                      {isAdmin && (
                        <Link
                          to="/admin"
                          onClick={() => setUserMenuOpen(false)}
                          className="tk-user-admin"
                          data-testid="header-admin-link"
                        >
                          <Shield size={14} /> Admin Panel
                        </Link>
                      )}
                      <Link to="/wishlist" onClick={() => setUserMenuOpen(false)}>
                        <Heart size={14} /> My Wishlist ({count})
                      </Link>
                      <button
                        type="button"
                        onClick={() => {
                          setUserMenuOpen(false);
                          logout();
                          navigate("/auth");
                        }}
                        data-testid="header-logout"
                      >
                        <LogOut size={14} /> Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="tk-user" ref={menuRef}>
                  <button
                    type="button"
                    className="tk-user-btn tk-guest-btn"
                    onClick={() => setUserMenuOpen((s) => !s)}
                    aria-haspopup="menu"
                    aria-expanded={userMenuOpen}
                    data-testid="header-guest"
                  >
                    <span className="tk-user-initial tk-guest-initial">
                      <User size={14} />
                    </span>
                    <span className="tk-user-name">Guest</span>
                    <ChevronDown size={13} />
                  </button>
                  {userMenuOpen && (
                    <div className="tk-user-menu" role="menu">
                      <div className="tk-user-info">
                        <strong>Browsing as Guest</strong>
                        <span>Sign in to save your wishlist &amp; get faster enquiries.</span>
                      </div>
                      <Link
                        to="/auth"
                        onClick={() => setUserMenuOpen(false)}
                        data-testid="header-login"
                      >
                        <User size={14} /> Sign In
                      </Link>
                      <Link
                        to="/auth?mode=register"
                        onClick={() => setUserMenuOpen(false)}
                        data-testid="header-register"
                      >
                        <Shield size={14} /> Create Account
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Row 2 — category nav (colour + opacity slider controlled from Admin > Hero) */}
        <div
          className={`tk-header-nav ${hero.subNavAnimation ? "tk-header-nav-shine" : ""}`}
          style={{
            background: hexToRgba(
              hero.subNavColor,
              Math.max(0, Math.min(100, hero.subNavOpacity)) / 100
            ),
          }}
          data-testid="sub-navbar"
        >
          <div className="wrap tk-nav-row">
            {TOP_NAV.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                className={`tk-nav-link ${l.eyebrow ? "tk-nav-eyebrow" : ""}`}
              >
                {l.label}
              </Link>
            ))}
            {store.phone && (
              <span
                className="tk-nav-phone"
                style={hero.subNavPhoneColor ? { color: hero.subNavPhoneColor } : undefined}
                data-testid="sub-nav-phone"
              >
                Call:{" "}
                <a
                  href={store.telHref}
                  style={hero.subNavPhoneColor ? { color: hero.subNavPhoneColor } : undefined}
                >
                  {store.phone}
                </a>
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Slide-in menu drawer (used from BOTH desktop menu button AND mobile burger) */}
      <div className={`tk-mobile ${open ? "open" : ""}`} data-testid="menu-drawer">
        <button
          className="tk-mobile-close"
          aria-label="Close menu"
          onClick={() => setOpen(false)}
        >
          <X size={30} />
        </button>
        {user && (
          <div className="tk-mp-user">
            Hi, {firstName}
            <button
              type="button"
              className="tk-mp-logout"
              onClick={() => {
                setOpen(false);
                logout();
                navigate("/auth");
              }}
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        )}
        <button
          type="button"
          className="tk-mp-search"
          onClick={() => {
            setOpen(false);
            setTimeout(() => setSearchOpen(true), 150);
          }}
        >
          <Search size={18} /> Search designs
        </button>
        <Link to="/wishlist" onClick={() => setOpen(false)}>
          <Heart size={16} /> Wishlist ({count})
        </Link>
        <a target="_top" rel="noreferrer" href={store.mapUrl} onClick={() => setOpen(false)}>
          <MapPin size={16} /> Visit Store
        </a>
        {store.facebook && (
          <a
            target="_top"
            rel="noreferrer"
            href={store.facebook}
            onClick={() => setOpen(false)}
            data-testid="drawer-facebook"
          >
            <Facebook size={16} /> Facebook
          </a>
        )}
        {!user && (
          <Link to="/auth" onClick={() => setOpen(false)}>
            <User size={16} /> Login
          </Link>
        )}
        <div className="tk-mp-divider">Categories</div>
        {TOP_NAV.map((l) => (
          <Link key={l.label} to={l.to} onClick={() => setOpen(false)}>
            {l.label}
          </Link>
        ))}
      </div>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
